/**
 * AdminSupplierGrid - AG Grid implementation for Supplier Management
 *
 * Displays suppliers in a sortable, filterable grid with:
 * - ID, Name, Subfolder, Client, Created Date
 * - Action buttons (Edit, Delete)
 */

import React, { useMemo } from "react";
import styled from "styled-components";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
  themeQuartz,
} from "ag-grid-community";
import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { IconButton } from "@flexport/latitude-typescript";

// Register AG Grid v34 modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Configure AG Grid v34 theme
const gridTheme = themeQuartz;

/**
 * Grid wrapper with fixed height
 */
const GridWrapper = styled.div`
  width: 100%;
  height: 600px;
  margin-top: 20px;
`;

/**
 * Tooltip wrapper that properly shows tooltip when hovering over button and its children
 * The title attribute on the wrapper will trigger tooltips for the entire area
 */
const TooltipWrapper = styled.div`
  display: inline-flex;
  position: relative;
  cursor: pointer;

  /* Ensure button inherits the cursor and doesn't block tooltip */
  button {
    cursor: pointer;
  }
`;

/**
 * Actions container for button spacing with centered alignment
 */
const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

/**
 * Supplier row data interface
 */
export interface SupplierRow {
  id: string;
  name: string;
  subfolder: string;
  clientId: string;
  client: {
    clientId: string;
    client_name: string;
    folder: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Props for AdminSupplierGrid
 */
interface AdminSupplierGridProps {
  rows: ReadonlyArray<SupplierRow>;
  onEdit: (supplier: SupplierRow) => void;
  onDelete: (id: string) => void;
}

/**
 * Date formatter for created date column
 */
const dateFormatter = (params: ValueFormatterParams<SupplierRow>): string => {
  const v = params.value;
  if (typeof v !== "string") return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString();
};

/**
 * Client name value getter
 */
const clientNameGetter = (params: ValueGetterParams<SupplierRow>): string => {
  return params.data?.client?.client_name || "—";
};

/**
 * Actions cell renderer - displays Edit and Delete icon buttons with tooltips
 */
const ActionsCellRenderer: React.FC<
  ICellRendererParams<SupplierRow> & {
    onEdit: (supplier: SupplierRow) => void;
    onDelete: (id: string) => void;
  }
> = (params) => {
  const { data } = params;
  const onEdit = (params as any).onEdit;
  const onDelete = (params as any).onDelete;

  if (!data) return null;

  return (
    <ActionsContainer>
      <TooltipWrapper title="Edit supplier details">
        <IconButton
          iconName="pencil"
          label="Edit"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="basic"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onEdit(data)}
          testId={`edit-supplier-${data.id}`}
        />
      </TooltipWrapper>
      <TooltipWrapper title="Delete supplier">
        <IconButton
          iconName="trash"
          label="Delete"
          labelAriaOnly={true}
          iconAlignment="left"
          intent="danger"
          kind="hollow"
          size="s"
          type="button"
          isLoading={false}
          width="responsive"
          height={{ type: "fixed" }}
          disabled={false}
          temporaryUnsafeSetSolidKindHack={false}
          onClick={() => onDelete(data.id)}
          testId={`delete-supplier-${data.id}`}
        />
      </TooltipWrapper>
    </ActionsContainer>
  );
};

/**
 * AdminSupplierGrid Component
 */
const AdminSupplierGrid: React.FC<AdminSupplierGridProps> = ({
  rows,
  onEdit,
  onDelete,
}) => {
  const columnDefs = useMemo<ColDef<SupplierRow>[]>(
    () => [
      {
        headerName: "ID",
        field: "id",
        width: 100,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Name",
        field: "name",
        flex: 1,
        minWidth: 200,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Subfolder",
        field: "subfolder",
        flex: 1,
        minWidth: 150,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Client",
        valueGetter: clientNameGetter,
        flex: 1,
        minWidth: 150,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Created",
        field: "createdAt",
        valueFormatter: dateFormatter,
        width: 130,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Actions",
        sortable: false,
        filter: false,
        width: 120,
        cellRenderer: ActionsCellRenderer,
        cellRendererParams: { onEdit, onDelete },
        suppressHeaderMenuButton: true,
      },
    ],
    [onEdit, onDelete],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressHeaderMenuButton: true,
    }),
    [],
  );

  return (
    <GridWrapper data-testid="supplier-grid">
      <AgGridReact<SupplierRow>
        theme={gridTheme}
        rowData={rows as SupplierRow[]}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        getRowId={(params) => params.data.id}
        suppressCellFocus={false}
      />
    </GridWrapper>
  );
};

export default AdminSupplierGrid;
